import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import LightSelect from "../../CompanyView/LightSelect";
import { UseCompanySubWorkspaces, UseCompanyWorkspaces } from "../../../hooks/company";
import {
  emptyRow, hoursWorkedKpiOf, includesSubWorkspaces, money, revenueKpiOf, revenueOf, rowsOf, workspaceIdsOf,
} from "../../../components/LaborMetrics/data";
import "./customize.css";

/**
 * Setup for the Payroll vs Revenue card.
 *
 * Two questions, in the order they matter: what the company made this period,
 * then what each department cost against it. Hours come from that workspace's
 * own Hours Worked metric — the technicians' one is fed by the HCP sync — and
 * a department with no Hours Worked metric (office, sales) gets a typed
 * number instead. Revenue is the same story: it syncs from the company's
 * Total Revenue metric when one exists, and falls back to typed only for a
 * company that hasn't added it. Total cost is hours × pay/hour once employee
 * rates are typed on the card; the typed "Total cost" field here is only the
 * fallback before that. The goal share is always typed — nothing in the app
 * knows that.
 */

const Pick = ({ value, options, onChange, placeholder }) => (
  <LightSelect
    width="100%"
    height={36}
    fontSize={12.5}
    radius={8}
    menuWidth={300}
    placeholder={placeholder}
    value={value || ""}
    options={options}
    onChange={(o) => onChange(o.value)}
  />
);
Pick.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
Pick.defaultProps = { value: "", placeholder: "Choose" };

const Num = ({ label, value, onChange, prefix, suffix, placeholder }) => (
  <label className="gmbw-lbr-num">
    <span>{label}</span>
    <span className="gmbw-lbr-numbox">
      {prefix ? <i>{prefix}</i> : null}
      <input
        type="number"
        min="0"
        step="any"
        inputMode="decimal"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix ? <i>{suffix}</i> : null}
    </span>
  </label>
);
Num.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  placeholder: PropTypes.string,
};
Num.defaultProps = { value: "", prefix: null, suffix: null, placeholder: "0" };

const Text = ({ label, value, onChange, placeholder }) => (
  <label className="gmbw-lbr-num">
    <span>{label}</span>
    <span className="gmbw-lbr-numbox">
      <input
        type="text"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  </label>
);
Text.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
Text.defaultProps = { value: "", placeholder: "" };

/* Setup's own picker slots, unlike data.js's workspaceIdsOf — that one drops
   blank entries because an empty id is never a real workspace to read from
   on the board, but Setup needs a blank slot to SURVIVE a render so "+
   Combine another workspace" has an empty picker to show. Using the
   filtered helper here made every blank slot vanish the instant it was
   added — clicking the button looked like it did nothing, because the very
   next render erased what it had just added. */
const setupMemberIds = (row) => {
  const ids = Array.isArray(row?.workspaceIds) ? row.workspaceIds.map((v) => String(v || "")) : [];
  if (ids.length) return ids;
  return row?.workspaceId ? [String(row.workspaceId)] : [];
};

const IcX = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const LaborSetup = ({ draft, onChange }) => {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const subWsByParent = UseCompanySubWorkspaces();

  const rows = rowsOf(draft);
  const typedRevenue = revenueOf(draft);

  const wsOptions = workspaces.map((w) => ({ value: String(w._id), label: w.name }));

  /* Revenue is company-wide, not per-workspace, so unlike Hours Worked this
     has to search every workspace's KPI list rather than one specific row's.
     Same "exactly one real source, nothing else is a plausible answer" rule:
     model.type "total_revenue" is the Total Revenue metric and nothing else
     is ever mistaken for it. */
  const totalRevenueKpi = () =>
    workspaces
      .map((w) => {
        const hit = (companyKpis?.[String(w._id)] || []).find(
          (k) => k && !k.isDraft && !k.disabled && (k.type === "total_revenue" || k?.model?.type === "total_revenue"),
        );
        return hit ? { workspaceId: String(w._id), kpi: hit } : null;
      })
      .find(Boolean) || null;

  const setRows = (next) => onChange({ ...draft, rows: next });
  const patchRow = (i, patch) => setRows(rows.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));

  /* Keeps hoursKpiId pointed at the real Hours Worked metric rather than
     trusting whatever was set at pick-time — covers a row created before
     companyKpis had loaded for that workspace (the picker's own onChange
     fires too early to see it yet), and self-heals if a workspace's Hours
     Worked metric is added, removed or swapped later. One setRows call, not
     one per row: patchRow reads the same `rows` closure for each call, so
     looping it here would let a later row's patch silently clobber an
     earlier one in the same pass. */
  useEffect(() => {
    const next = rows.map((r) => {
      const primaryId = workspaceIdsOf(r)[0];
      if (!primaryId) return r;
      const want = hoursWorkedKpiOf(companyKpis, primaryId) || "";
      return (r.hoursKpiId || "") === want ? r : { ...r, hoursKpiId: want };
    });
    if (next.some((r, i) => r !== rows[i])) setRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows.map((r) => workspaceIdsOf(r)[0])), companyKpis]);

  /* Same self-heal as the rows effect above, but for the one company-wide
     revenue reference instead of a per-row one — covers companyKpis loading
     late, and self-heals if the Total Revenue metric is added, removed or
     swapped later. */
  useEffect(() => {
    const found = totalRevenueKpi();
    const wantWs = found ? found.workspaceId : "";
    const wantKpi = found ? String(found.kpi._id) : "";
    if ((draft?.revenueWorkspaceId || "") === wantWs && (draft?.revenueKpiId || "") === wantKpi) return;
    onChange({ ...draft, revenueWorkspaceId: wantWs || undefined, revenueKpiId: wantKpi || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyKpis, JSON.stringify(workspaces.map((w) => w._id))]);

  const revKpi = totalRevenueKpi();
  /* Setup has no period window to sum a live figure against, so the row-level
     "% of revenue" preview below only ever has a real number to work with in
     the typed-fallback case — skipped (not NaN'd) once revenue is live. */
  const previewRevenue = revKpi ? 0 : typedRevenue;

  return (
    <>
      <div className="gmbw-fld">
        <label>Total revenue</label>
        {revKpi ? (
          /* Not a choice — a company either has the Total Revenue metric or
             it doesn't. Same locked-field pattern Hours Worked uses below for
             "this is synced, not typed here" values. */
          <div className="gmbw-lockedfield">
            <span>Total Revenue</span>
          </div>
        ) : (
          <div className="gmbw-lbr-solo">
            <Num
              label="What the company made this period"
              value={draft?.revenue ?? ""}
              prefix="$"
              onChange={(v) => onChange({ ...draft, revenue: v })}
            />
          </div>
        )}
        <p className="gmbw-hint">
          {revKpi
            ? "Synced from your Total Revenue metric — every department below is measured against this one figure, not against its own team's revenue, the way the payroll sheet does it."
            : "Every department below is measured against this one figure — not against its own team's revenue, the way the payroll sheet does it. Add a \"Total Revenue\" metric (Add Metric → Housecall Pro) to sync this automatically instead of typing it."}
        </p>
      </div>

      <div className="gmbw-fld">
        <label>Departments</label>
        {rows.map((row, i) => {
          const memberIds = setupMemberIds(row);
          /* Two or more workspaces on one line — the "Others" catch-all:
             whichever workspaces don't rate their own row, combined so their
             hours and cost add into a single department instead of each
             needing a line of its own. */
          const combined = memberIds.length > 1;
          const primaryId = memberIds[0] || "";
          const hk = hoursWorkedKpiOf(companyKpis, primaryId);
          /* Driven by the live-detected `hk`, not the stored `hoursKpiId` —
             the picker's onChange resets hoursKpiId to "" the instant a
             workspace is chosen, and the self-healing effect above only
             catches up a render later. Deciding both this and the locked-
             field/typed-input choice below off the same `hk` means there's
             no render where they can disagree — briefly showing BOTH the
             locked field and the manual hours input at once. A combined row
             always shows the typed field too — each member is checked for
             its own Hours Worked metric on the board, this is only ever the
             fallback for whichever ones don't have one. */
          const typed = combined || !hk;
          const cost = Number(row.totalCost) || 0;
          const goalPct = Number(row.goalPct) || 0;
          const subs = memberIds.flatMap((id) => subWsByParent?.[String(id)] || []);
          const includeSubs = includesSubWorkspaces(row);

          const setMember = (mi, wsId) => {
            const ids = memberIds.slice();
            ids[mi] = wsId;
            const cleaned = ids.filter(Boolean);
            patchRow(i, { workspaceIds: cleaned, workspaceId: cleaned[0] || "", hoursKpiId: "" });
          };
          const removeMember = (mi) => {
            const ids = memberIds.filter((_, ix) => ix !== mi);
            patchRow(i, { workspaceIds: ids, workspaceId: ids[0] || "" });
          };

          return (
            <div className="gmbw-mrow" key={row.key || `row${i}`}>
              {/* One workspace still names the department by itself — the
                  picker below IS the row's identity then. Two or more need
                  the typed "Group name" field further down instead. */}
              {(memberIds.length ? memberIds : [""]).map((wsId, mi) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="gmbw-mrow-top" key={mi} style={mi > 0 ? { marginTop: 6 } : undefined}>
                  <div className="gmbw-lbr-pick">
                    <Pick
                      value={String(wsId || "")}
                      options={wsOptions}
                      placeholder={mi === 0 ? "Which workspace" : "Combine with…"}
                      onChange={(v) => setMember(mi, v)}
                    />
                  </div>
                  <button
                    type="button"
                    className="gmbw-mrow-x"
                    aria-label={memberIds.length > 1 ? "Remove this workspace" : "Remove this department"}
                    onClick={() => (memberIds.length > 1 ? removeMember(mi) : setRows(rows.filter((_, ix) => ix !== i)))}
                  >
                    {IcX}
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="gmbw-addrow"
                style={{ marginTop: 8, padding: 8 }}
                onClick={() => patchRow(i, { workspaceIds: [...memberIds, ""] })}
              >
                + Combine another workspace
              </button>

              {combined && (
                <div className="gmbw-lbr-grid" style={{ marginTop: 10 }}>
                  <div className="gmbw-lbr-full">
                    <Text
                      label="Group name"
                      value={row.label || ""}
                      placeholder="e.g. Others, Support Staff, Overhead"
                      onChange={(v) => patchRow(i, { label: v })}
                    />
                    <p className="gmbw-hint" style={{ margin: "-4px 0 0" }}>
                      This is a group of {memberIds.filter(Boolean).length} workspaces now — name it
                      whatever fits. Left blank, it shows as &quot;Others&quot; on the card.
                    </p>
                  </div>
                </div>
              )}

              {subs.length > 0 && (
                <div className="gmbw-toggle-block">
                  <div className="gmbw-toggle noted">
                    <span>
                      Include sub-workspaces
                      <i className="gmbw-toggle-note">
                        {`Rolls ${subs.map((w) => w.name).join(", ")} into this department's roster and hours. Off counts only ${memberIds.map((id) => workspaces.find((w) => String(w._id) === id)?.name).filter(Boolean).join(" + ") || "the workspace(s) above"} itself.`}
                      </i>
                    </span>
                    <div className="gmbw-toggle-ctl">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={includeSubs}
                        aria-label="Include sub-workspaces"
                        className={`gmbw-switch${includeSubs ? " on" : ""}`}
                        onClick={() => patchRow(i, { includeSubWorkspaces: !includeSubs })}
                      >
                        <span className="gmbw-switch-dot" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="gmbw-lbr-grid">
                {!combined && !!primaryId && (
                  <div className="gmbw-lbr-full">
                    {hk ? (
                      /* Not a choice — the workspace only has one Hours
                         Worked metric, so there's nothing to pick between.
                         Same locked-field pattern Basics uses for "this is
                         derived, not typed here" values. */
                      <div className="gmbw-lockedfield">
                        <span>Hours Worked</span>
                      </div>
                    ) : (
                      <p className="gmbw-hint" style={{ margin: "0 0 2px" }}>
                        This workspace doesn&apos;t track Hours Worked — type the hours below.
                      </p>
                    )}
                  </div>
                )}
                {combined && (
                  <p className="gmbw-hint" style={{ margin: "0 0 2px", gridColumn: "1 / -1" }}>
                    Each combined workspace&apos;s own Hours Worked metric is counted
                    automatically — type hours below only as a fallback for
                    whichever ones don&apos;t track it.
                  </p>
                )}

                {typed && (
                  <Num
                    label="Hours"
                    value={row.hours}
                    suffix="hrs"
                    onChange={(v) => patchRow(i, { hours: v })}
                  />
                )}
                <Num
                  label="Total cost (fallback)"
                  value={row.totalCost}
                  prefix="$"
                  onChange={(v) => patchRow(i, { totalCost: v })}
                />
                <p className="gmbw-hint" style={{ margin: "-4px 0 0", gridColumn: "1 / -1" }}>
                  Total cost is hours × pay/hour once employee rates are typed
                  on the card — this figure only counts before that.
                </p>
                <Num
                  label="Goal"
                  value={row.goalPct}
                  suffix="% of rev"
                  onChange={(v) => patchRow(i, { goalPct: v })}
                />
              </div>

              {/* The figures the card works out from what was typed here, shown
                  so a wrong entry is caught in Setup, not on the board. Pay/hour
                  isn't previewable here — it's typed per employee on the card. */}
              {cost > 0 && previewRevenue > 0 && (
                <p className="gmbw-lbr-math">
                  <b>{((cost / previewRevenue) * 100).toFixed(1)}%</b> of revenue
                  {goalPct > 0 && <> · goal {money(previewRevenue * (goalPct / 100))}</>}
                </p>
              )}
            </div>
          );
        })}
        <button type="button" className="gmbw-addrow" onClick={() => setRows([...rows, emptyRow()])}>
          + Add a department
        </button>
        <p className="gmbw-hint">
          Pay/hour isn&apos;t set here — expand a department on the card itself and
          type each employee&apos;s rate; the department&apos;s figure is the average of
          those. An employee paid monthly instead of hourly can switch to
          Monthly there too — it converts to an hourly-equivalent using that
          month&apos;s own weekday count. Goal is the share of revenue this
          department is allowed to cost: over it reads red on the card, under
          it reads green.
        </p>
      </div>
    </>
  );
};

LaborSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default LaborSetup;

/** True once the card has enough to draw something real. */
export const laborConfigured = (opts) =>
  rowsOf(opts).length > 0 && (!!revenueKpiOf(opts) || revenueOf(opts) > 0);

/* Every formula the card runs, in the order data.js derives them — Setup
   already shows each row's OWN math once it's typed in (.gmbw-lbr-math,
   above); this is the general reference for Basics, read before there's
   anything to plug numbers into yet. Total cost and Goal % are typed by
   hand — Hours and Revenue each either sync live from their own metric or
   are typed too. Pay/hour is the one figure NOT typed here — it's each
   employee's own rate, typed on the card, averaged. */
const FORMULAS = [
  { out: "Pay/hour", terms: ["average of", "Employee pay/hour"] },
  { out: "Total cost", terms: ["Hours", "×", "Pay/hour"] },
  { out: "Performance", terms: ["Total cost", "÷", "Revenue"] },
  { out: "Goal $", terms: ["Goal %", "×", "Revenue"] },
  { out: "Over/Under", terms: ["Goal %", "−", "Performance"] },
  { out: "Over/Under $", terms: ["Total cost", "−", "Goal $"] },
];

const IcSigma = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 5H7l6 7-6 7h11" />
  </svg>
);

export const LaborFormulaCard = () => (
  <div className="gmbw-formula">
    <div className="gmbw-formula-h">
      <span className="gmbw-formula-ic">{IcSigma}</span>
      How every column is calculated
    </div>
    <dl className="gmbw-formula-list">
      {FORMULAS.map((f) => (
        <div className="gmbw-formula-row" key={f.out}>
          <dt>{f.out}</dt>
          <dd>
            {f.terms.map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className={/^[÷×−+]$/.test(t) ? "op" : "term"}>{t}</span>
            ))}
          </dd>
        </div>
      ))}
    </dl>
    <p className="gmbw-formula-note">
      <b>Goal %</b> is typed by hand here. <b>Hours</b> and <b>Revenue</b> each
      either sync live from their own metric or are typed the same way.
      <b> Employee pay/hour</b> is typed per employee on the card, not
      here — <b>Total cost</b> is worked out from those two (hours typed here
      falls back only until pay rates are entered).
    </p>
  </div>
);
